import { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Camera, LayoutGrid } from 'lucide-react';
import { CheckinPost } from './components/CheckinPost';
import { CookFeedCard } from './components/CookFeedCard';
import { GameFeedCard } from './components/GameFeedCard';
import { buildFeedItems, groupFeedItemsByDay, itemId, type FeedItem } from './feedItems';
import { useCooksForFeed } from './cooks';
import { useGamesForFeed } from './games';
import { useMembers } from './members';
import { usePendingPhotos, useCheckins } from './useCheckins';

/**
 * The feed (design: "Screen F — Feed, newest first").
 *
 * Strictly chronological, grouped by day, and that is the entire ordering logic. The
 * camera button is the only way to post — there is no compose field at the top, because
 * the thing you came to do is take a picture.
 *
 * Finished games and cooks are interleaved into this same chronology as quiet fact cards
 * (`feedItems.ts`, `GameFeedCard`, `CookFeedCard`) — a read-side merge of `CHECKINS_DOC`,
 * `GAMES_DOC` and `COOKS_DOC`, retroactive by construction: a game finished last month
 * appears in last month's day group the first time this page renders, with no backfill
 * step required.
 */

const SCROLL_KEY = 'awwyeah:checkin:feed-scroll';

export function CheckinFeedPage(): JSX.Element {
  const navigate = useNavigate();
  const { checkins, markSeen, selfId } = useCheckins();
  const games = useGamesForFeed();
  const cooks = useCooksForFeed();
  const members = useMembers();
  const pending = usePendingPhotos();

  const queuedPhotoIds = useMemo(() => new Set(pending.map((record) => record.id)), [pending]);
  const items = useMemo(() => buildFeedItems(checkins, games, cooks), [checkins, games, cooks]);
  const groups = useMemo(() => groupFeedItemsByDay(items), [items]);
  const memberCount = members.all().length;

  useFeedScrollRestore(checkins.length > 0);

  /**
   * One row of the feed. A `switch` on `item.kind`, not a ternary chain: this used to be a
   * two-way "checkin or else game" ternary, which quietly rendered a cook as a game the
   * moment a third kind existed. With the switch, `FeedItem` gaining a fourth variant is a
   * compile error right here — which is exactly where someone adding one needs to look.
   */
  function renderItem(item: FeedItem): JSX.Element {
    switch (item.kind) {
      case 'checkin':
        return (
          <CheckinPost
            key={itemId(item)}
            checkin={item.checkin}
            members={members}
            memberCount={memberCount}
            selfId={selfId}
            queued={Boolean(item.checkin.photoRef && queuedPhotoIds.has(item.checkin.photoRef.id))}
            onSeen={markSeen}
          />
        );
      case 'game':
        return <GameFeedCard key={itemId(item)} game={item.game} members={members} />;
      case 'cook':
        return <CookFeedCard key={itemId(item)} cook={item.cook} members={members} />;
    }
  }

  return (
    <div
      className="-mx-4 flex min-h-[60vh] flex-col lg:mx-auto lg:max-w-2xl"
      data-testid="checkin-feed"
    >
      <header className="flex items-center justify-between border-b border-line bg-surface px-4">
        <h2 className="font-display text-[22px] tracking-tight text-fg">Check-in</h2>
        <Link
          to="/checkin/archive"
          className="flex h-11 w-11 items-center justify-center text-fg-2"
          aria-label="Archive"
          data-testid="checkin-archive-link"
        >
          <LayoutGrid size={20} strokeWidth={1.75} />
        </Link>
      </header>

      {items.length === 0 ? (
        <EmptyFeed onCheckIn={() => navigate('/checkin/new')} />
      ) : (
        <div className="flex flex-col pb-28">
          {groups.map((group) => (
            <section key={group.key} className="flex flex-col gap-2 pt-2">
              <h3 className="px-4 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-fg-3">
                {group.label}
              </h3>
              <div className="flex flex-col gap-2">{group.items.map(renderItem)}</div>
            </section>
          ))}
        </div>
      )}

      <Link
        to="/checkin/new"
        data-testid="checkin-composer-button"
        aria-label="Check in"
        className="fixed bottom-[calc(72px+env(safe-area-inset-bottom))] right-4 z-10 flex h-14 w-14 items-center justify-center rounded-full bg-inverse text-inverse-fg shadow-[0_8px_28px_-12px_rgba(18,18,18,0.35)] transition-transform duration-150 ease-app active:scale-95 lg:bottom-8"
      >
        <Camera size={24} strokeWidth={1.75} />
      </Link>
    </div>
  );
}

/** Design: "invites something dumb rather than something good." */
function EmptyFeed({ onCheckIn }: { onCheckIn: () => void }): JSX.Element {
  return (
    <div
      className="flex flex-1 flex-col justify-center gap-2.5 px-5 py-16"
      data-testid="checkin-empty"
    >
      <div className="w-12 border-t-2 border-fg" />
      <div className="text-[19px] font-bold tracking-tight text-fg">Quiet in here</div>
      <div className="text-[15px] leading-[1.45] text-fg-2">
        Take a picture of something dumb. It doesn’t have to be good.
      </div>
      <button
        type="button"
        onClick={onCheckIn}
        className="mt-2 h-12 self-start rounded-md bg-inverse px-5 text-[15px] font-semibold text-inverse-fg"
      >
        Check in
      </button>
    </div>
  );
}

/**
 * Coming back from a photo returns you to the same place in the feed — the design says
 * "Back returns you to the same scroll position", and a feed that jumps to the top is a
 * feed you stop scrolling.
 */
function useFeedScrollRestore(ready: boolean): void {
  useEffect(() => {
    if (!ready) return;
    const saved = Number(sessionStorage.getItem(SCROLL_KEY) ?? '0');
    if (saved > 0) window.scrollTo(0, saved);

    const remember = (): void => sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
    window.addEventListener('scroll', remember, { passive: true });
    return () => {
      remember();
      window.removeEventListener('scroll', remember);
    };
  }, [ready]);
}
